import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ModalService } from '../../../services/modal.service';
import { Observable, forkJoin } from 'rxjs';

import { ZSNConsoleModulationType } from '../../../models/zsn-lists.model';
import { ZSNCampaign, ZSNOrderItem, ZSNParcel } from '../../../models/zsn.model';
import { CampaignYearPipe } from '../../../pipes/campaignYear.pipe';
import { CapitalizePipe } from '../../../pipes/capitalize.pipe';
import { Tooltip } from '../../../utils/Tooltip';
import { TOOLTIP_TEXTS } from '../../../utils/tooltip-texts';
import { TooltipContent } from '../../../utils/TooltipContent';
// FIXME mettre en commun avec tous les outils
import { ARConsoleFormatType, ARConsoleType, ARConsoleVersion } from '../../../models/agrorendement-common-lists.model';
import { ARVraData } from '../../../models/agrorendement-common.model';
import { ListItem } from '../../../models/lists.model';
import { ARConsoleService } from '../../../services/agrorendement-common.service';
import { ParcelService } from '../../../services/parcel.service';
import { ZSNListsService } from '../../../services/zsn-lists.service';
import { ZSNService } from '../../../services/zsn.service';
import { getBatchOrderItems, prepareBatchSave } from '../../../utils/batchedit.utils';
import { PreventDoubleClickDirective } from '../../../utils/preventDoubleClick';
import { showErrorMessage, toInteger, toNumber } from '../../../utils/utils';
import { ARVraEditComponent } from '../agrorendement/common/vra-edit.component';
import { ZSNUtils } from './zsn.utils';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { FarmerAuthorizationsService } from '../../../services/farmer-authorizations.service';
import { isFarmerActionForbidden } from '../../../utils/farmer-action-error';
import { isExistingOrder, isOrderSaveForbidden } from '../../../utils/order-save-forbidden';
import { I18nTranslator } from '../../../i18n/translate';

@Component({
  standalone: true,
  selector: 'zsn-orderItem-edit',
  templateUrl: 'orderItem-edit.component.html',
  imports: [
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    CommonModule,
    Tooltip,
    TooltipContent,
    ARVraEditComponent,
    PreventDoubleClickDirective,
    CampaignYearPipe,
    CapitalizePipe,
    TranslatePipe,
    FormsModule,
    TooltipModule,
  ],
  providers: [ZSNService, ARConsoleService, ParcelService],
})
export class ZSNOrderItemEditComponent implements OnInit, OnDestroy {
  @Input() orderItem: ZSNOrderItem;
  error: any;
  sub: any;

  edited = false;
  withinOrder = false;

  orderItemId: number;
  parcel: ZSNParcel;

  currentCampaign: ZSNCampaign;
  vraData: ARVraData;

  isShowModulationZones = false;

  isBatch = false;
  batchOrderItems: ZSNOrderItem[];
  cbBatchEdit: Object;

  tooltipTexts = TOOLTIP_TEXTS;

  // Block 6 (C2027-196): whether the organisation lets this farmer-only user save this order.
  canCreateOrder = true;
  canEditOrder = true;
  saveForbidden = false;
  orderIsBeingEdited = false;

  constructor(
    private zsnService: ZSNService,
    private zsnListService: ZSNListsService,
    private parcelService: ParcelService,
    private modal: ModalService,
    private router: Router,
    private route: ActivatedRoute,
    private farmerAuthorizationsService: FarmerAuthorizationsService,
    private i18nTranslator: I18nTranslator,
  ) {
    this.farmerAuthorizationsService.can('createOrder').subscribe((allowed) => {
      this.canCreateOrder = allowed;
      this.updateSaveForbidden();
    });
    this.farmerAuthorizationsService.can('editOrder').subscribe((allowed) => {
      this.canEditOrder = allowed;
      this.updateSaveForbidden();
    });
  }

  updateSaveForbidden() {
    this.saveForbidden = isOrderSaveForbidden(
      this.isBatch,
      this.orderItem,
      this.batchOrderItems,
      this.canCreateOrder,
      this.canEditOrder,
    );
    this.orderIsBeingEdited = isExistingOrder(this.isBatch, this.orderItem, this.batchOrderItems);
  }

  ngOnInit() {
    this.sub = this.route.params.subscribe((params) => {
      if (params['parcelId']) {
        if (this.route.snapshot.url[3].path === 'edit') {
          const parcelId = +params['parcelId'];
          const obs0 = this.zsnService.getParcel(parcelId);
          obs0.subscribe((x: ZSNParcel) => {
            if (x.orderItem) {
              this.orderItemId = x.orderItem.id;
              this.edited = true;
              // FIXME traiter les erreurs ; afficher un message
              const obs1 = this.zsnService.getOrderItem(this.orderItemId);
              const obs2 = this.zsnService.getCampaigns();
              forkJoin([obs1, obs2]).subscribe(
                (data: any) => {
                  this.orderItem = data[0];
                  const campaigns: ZSNCampaign[] = data[1];
                  this.parcel = this.orderItem.parcel;
                  this.updateSaveForbidden();
                  this.prepareOrderItem();
                  this.handleCampaigns(campaigns);
                },
                (e) => this.handleGetError(e),
              );
            } else {
              this.parcel = x;
              this.edited = false;
              const obs2 = this.zsnService.getCampaigns();
              this.newOrderItem(parcelId);
              this.updateSaveForbidden();
              obs2.subscribe((campaigns: any) => {
                this.handleCampaigns(campaigns);
              });
            }
          });
        } else {
          showErrorMessage(this.modal, 'Pas de parcelle spécifiée').then((r) => {
            this.router.navigate(['/orders', 'zsn', 'parcels']);
          });
        }
      } else if (this.route.snapshot.url[3].path === 'batch') {
        this.isBatch = true;
        this.edited = true;
        const parcelIds = params['parcelIds'].split('-').map((x: string) => +x);
        getBatchOrderItems(
          this.parcelService,
          this.zsnService,
          parcelIds,
          this,
          'ZSN',
          () => {
            return new ZSNOrderItem();
          },
          () => {
            return new ZSNParcel();
          },
          ['zsn'],
          () => this.updateSaveForbidden(),
        );

        this.zsnService.getCampaigns().subscribe((campaigns: any[]) => {
          this.handleCampaigns(campaigns);
        });
      }
    });
  }

  goBack(savedConfig: ZSNOrderItem = null) {
    this.router.navigate(['/orders', 'zsn', 'parcels']);
    return false;
  }

  save() {
    let isValid = true;

    // on nettoie sinon .id = null pas valide sur le serveur
    let restoreConsoleModulationType = false;
    if (!this.orderItem.consoleModulationType.id) {
      restoreConsoleModulationType = true;
      this.orderItem.consoleModulationType = null;
    }
    let restoreConsoleFormatType = false;
    if (!this.orderItem.consoleFormatType.id) {
      restoreConsoleFormatType = true;
      this.orderItem.consoleFormatType = null;
    }
    let restoreConsoleType = false;
    if (!this.orderItem.consoleType.id) {
      restoreConsoleType = true;
      this.orderItem.consoleType = null;
    }
    let restoreConsoleVersion = false;
    if (!this.orderItem.consoleVersion.id) {
      restoreConsoleVersion = true;
      this.orderItem.consoleVersion = null;
    }

    console.log(typeof this.orderItem.numZones);

    try {
      this.orderItem.numZones = toInteger(this.orderItem.numZones);
      this.orderItem.zone1 = toNumber(this.orderItem.zone1);
      this.orderItem.zone2 = toNumber(this.orderItem.zone2);
      this.orderItem.zone3 = toNumber(this.orderItem.zone3);
      if (this.orderItem.numZones === 6) {
        this.orderItem.zone4 = toNumber(this.orderItem.zone4);
        this.orderItem.zone5 = toNumber(this.orderItem.zone5);
        this.orderItem.zone6 = toNumber(this.orderItem.zone6);
      } else if (this.orderItem.numZones === 3) {
        this.orderItem.zone4 = this.orderItem.zone5 = this.orderItem.zone6 = null;
      }
    } catch (e) {
      isValid = false;
      showErrorMessage(this.modal, 'Il y a des nombres invalides dans le formulaire');
    }

    if (isValid) {
      if (this.isBatch) {
        const obss: Observable<ZSNOrderItem>[] = [];
        prepareBatchSave(this.orderItem, this.batchOrderItems, this.cbBatchEdit).forEach((batchOrderItem: any) => {
          obss.push(this.zsnService.saveOrderItem(batchOrderItem));
        });

        forkJoin(obss).subscribe(
          (orderItems) => {
            this.goBack();
          },
          (error) => this.handleSavebatchError(error),
        );
      } else {
        this.zsnService.saveOrderItem(this.orderItem).subscribe(
          (orderItem) => {
            if (!this.withinOrder) {
              this.goBack(orderItem);
            } else {
              this.confirmOrder(orderItem.parcel.id);
            }
          },
          (error) => this.handleSaveError(error),
        );
      }
    }

    if (restoreConsoleModulationType) {
      this.orderItem.consoleModulationType = new ZSNConsoleModulationType();
      this.orderItem.consoleModulationType.id = null;
    }
    if (restoreConsoleFormatType) {
      this.orderItem.consoleFormatType = new ARConsoleFormatType();
      this.orderItem.consoleFormatType.id = null;
    }
    if (restoreConsoleType) {
      this.orderItem.consoleType = new ARConsoleType();
      this.orderItem.consoleType.id = null;
    }
    if (restoreConsoleVersion) {
      this.orderItem.consoleVersion = new ARConsoleVersion();
      this.orderItem.consoleVersion.id = null;
    }
  }

  getParcel(parcelId: number) {
    this.zsnService.getParcel(parcelId).subscribe(
      (parcel) => {
        this.parcel = parcel;
      },
      (error) => this.handleGetError(error),
    );
  }

  getCampaigns() {
    this.zsnService.getCampaigns().subscribe((data) => {
      this.handleCampaigns(data);
    });
  }

  onChangeNumZones(numZones: number) {
    if (+numZones === 3) {
      this.orderItem.zone4 = this.orderItem.zone5 = this.orderItem.zone6 = null;
    }
  }

  handleCampaigns(campaigns: ZSNCampaign[]) {
    campaigns = campaigns.filter((x) => ZSNUtils.isCampaignCurrentlyOpen(x));
    if (this.isBatch) {
      // on suppose qu'on est dans la dernière campagne
      // TODO  vérifier avec les parcelles; normalement pas éditable sinon
      if (campaigns.length > 0) {
        this.currentCampaign = campaigns[0];
      }
    } else {
      for (const campaign of campaigns) {
        if (campaign.id === this.parcel.campaign.id) {
          this.currentCampaign = campaign;
        }
      }
    }
  }

  newOrderItem(parcelId: number) {
    this.orderItem = new ZSNOrderItem();
    this.orderItem.parcel = new ZSNParcel();

    this.orderItem.parcel.id = parcelId;

    this.prepareZoneData();
  }

  prepareZoneData() {
    this.orderItem.numZones = null;
    this.orderItem.zone1 = null;
    this.orderItem.zone2 = null;
    this.orderItem.zone3 = null;
    this.orderItem.zone4 = null;
    this.orderItem.zone5 = null;
    this.orderItem.zone6 = null;
  }

  prepareOrderItem() {
    if (!this.orderItem.numZones) {
      this.orderItem.numZones = null;
    } else {
      this.onChangeNumZones(this.orderItem.numZones);
    }
  }

  addOrder() {
    this.withinOrder = true;
    this.save();
  }

  confirmOrder(parcelId: number) {
    this.router.navigate(['/orders', 'zsn', 'orders', 'new', parcelId]);
  }

  handleSaveError(error: any) {
    if (isFarmerActionForbidden(error)) {
      showErrorMessage(this.modal, this.i18nTranslator.translate('error.farmerActionForbidden'));
    } else if (error.status === 401) {
      showErrorMessage(this.modal, "Vous n'êtes pas autorisé à modifier cette parcelle");
    } else {
      // FIXME détailler les erreurs du formulaire
      // FIXME définir un format pour les erreurs de validation
      console.error(error);
      showErrorMessage(this.modal, 'Il y a des erreurs dans le formulaire');
    }
  }

  handleSavebatchError(error: any) {
    if (error.status === 401) {
      showErrorMessage(this.modal, "Vous n'êtes pas autorisé à modifier cette parcelle");
    } else {
      // FIXME détailler les erreurs du formulaire
      // FIXME définir un format pour les erreurs de validation
      console.error(error);
      showErrorMessage(this.modal, 'Impossible de sauver les informations agronomiques pour certaines parcelles');
    }
  }

  handleGetError(error: any) {
    if (error.status === 401) {
      showErrorMessage(this.modal, "Vous n'êtes pas autorisé à consulter cette parcelle").then((r) => {
        this.router.navigate(['/orders', 'zsn', 'parcels']);
      });
    } else {
      console.error(error);
      showErrorMessage(this.modal, 'Une erreur est survenue').then((r) => {
        this.router.navigate(['/orders', 'zsn', 'parcels']);
      });
    }
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

  onModulationTypeChange(modulationType: ListItem) {
    this.isShowModulationZones = modulationType.code === 'AUTO_MODULATION';
  }
}
